import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ReactDOM from 'react-dom';
import api from '../../services/api';
import './Header.css';

/**
 * Enhanced Header component with integrated authentication support
 * Maintains existing design while adding secure user management features
 * Responsive design with mobile-first approach
 */
const Header = ({
  currentUser,
  onLogout,
  onShowPasswordModal,
  isAuthenticated = false
}) => {
  const location = useLocation();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    // Get dark mode preference from localStorage
    return localStorage.getItem('darkMode') === 'true';
  });
  const userButtonRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const [userNotificationCount, setUserNotificationCount] = useState(0);

  /**
   * Navigation items configuration with role-based access control
   * Dynamically shows/hides items based on user permissions
   */
  const getNavigationItems = () => {
    const baseNavItems = [
      { path: '/', label: 'Dashboard', icon: 'fas fa-tachometer-alt' },
      { path: '/customers', label: 'Customers', icon: 'fas fa-users' },
      { path: '/estimates', label: 'Estimates', icon: 'fas fa-file-alt' },
      { path: '/bids', label: 'Bids', icon: 'fas fa-dollar-sign' },
      { path: '/schedule', label: 'Schedule', icon: 'fas fa-calendar-alt' }
    ];

    // Add admin-only navigation items
    if (currentUser?.role === 'admin') {
      baseNavItems.push({
        path: '/users',
        label: 'User Management',
        icon: 'fas fa-user-cog',
        adminOnly: true
      });
    }

    return baseNavItems;
  };

  /**
   * Check if current route matches navigation item
   * Supports exact matching and nested route detection
   */
  const isActiveRoute = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  /**
   * Handle user dropdown toggle with click outside handling
   */
  const toggleUserDropdown = () => {
    setShowUserDropdown(!showUserDropdown);
  };

  /**
   * Handle mobile menu toggle
   */
  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  /**
   * Handle user logout with cleanup
   */
  const handleLogout = async () => {
    setShowUserDropdown(false);
    setShowMobileMenu(false);
    await onLogout();
  };

  /**
   * Handle password change modal opening
   */
  const handlePasswordChange = () => {
    setShowUserDropdown(false);
    onShowPasswordModal();
  };

  /**
   * Handle click outside dropdown to close it
   */
  const handleClickOutside = (event) => {
    if (!event.target.closest('.user-dropdown-container')) {
      setShowUserDropdown(false);
    }
  };

  /**
   * Toggle dark mode and save preference
   */
  const toggleDarkMode = () => {

    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    // Use both Bootstrap's data-bs-theme and custom data-theme
    document.documentElement.setAttribute('data-bs-theme', newDarkMode ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', newDarkMode ? 'dark' : 'light');

  };

  // Add click outside listener
  useEffect(() => {
    if (showUserDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showUserDropdown]);

  // Apply dark mode on mount
  useEffect(() => {
    // Use both Bootstrap's data-bs-theme and custom data-theme
    document.documentElement.setAttribute('data-bs-theme', darkMode ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // Calculate dropdown position when it opens
  useEffect(() => {
    if (showUserDropdown && userButtonRef.current) {
      const rect = userButtonRef.current.getBoundingClientRect();
      const dropdownWidth = 320;
      const rightPosition = Math.max(16, window.innerWidth - rect.right);

      setDropdownStyle({
        position: 'fixed',
        top: `${rect.bottom + 8}px`,
        right: `${rightPosition}px`,
        zIndex: 99999,
        maxWidth: `${Math.min(dropdownWidth, window.innerWidth - 32)}px`
      });
    }
  }, [showUserDropdown]);

  // Fetch user notification count for admin users
  const fetchUserNotificationCount = async () => {
    if (!isAuthenticated || !currentUser || currentUser.role !== 'admin') {
      return;
    }

    try {
      const token = localStorage.getItem('scott_access_token');
      if (!token) return;

      const response = await api.get('/auth/users/notification-count');
      setUserNotificationCount(response.data.count || 0);
    } catch (error) {
      // Silently fail - this is a non-critical feature
      console.debug('Failed to fetch user notification count:', error);
    }
  };

  // Fetch notification count on mount and periodically
  useEffect(() => {
    if (isAuthenticated && currentUser && currentUser.role === 'admin') {
      // Fetch immediately
      fetchUserNotificationCount();

      // Fetch every 60 seconds
      const interval = setInterval(fetchUserNotificationCount, 60000);

      return () => clearInterval(interval);
    }
  }, [isAuthenticated, currentUser]);

  const navigationItems = getNavigationItems();

  return (
    <header className="header-container">
      <div className="header-content">
        {/* Logo and Company Name - MODIFIED: Logo div removed */}
        <div className="header-brand">
          <Link to="/" className="brand-link">
            {/* The brand-logo div was here and has been removed */}
            <div className="brand-text">
              <h1 className="brand-title">Scott Overhead Doors</h1>
              <span className="brand-subtitle">Professional Door Solutions</span>
            </div>
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="desktop-navigation">
          <ul className="nav-list">
            {navigationItems.map((item) => (
              <li key={item.path} className="nav-item">
                <Link
                  to={item.path}
                  className={`nav-link ${isActiveRoute(item.path) ? 'active' : ''} ${
                    item.adminOnly ? 'admin-only' : ''
                  }`}
                  title={item.adminOnly ? 'Admin Only' : item.label}
                >
                  <i className={`${item.icon} nav-icon`}></i>
                  <span className="nav-text">{item.label}</span>
                  {item.adminOnly && userNotificationCount > 0 && (
                    <span className="admin-badge notification-dot">
                      <i className="fas fa-crown"></i>
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* User Profile Section */}
        {isAuthenticated && currentUser && (
          <div className="user-section">
            {/* Dark Mode Toggle */}
            <label className="theme-switch" title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
              <input
                type="checkbox"
                checked={darkMode}
                onChange={toggleDarkMode}
                aria-label="Toggle dark mode"
              />
              <span className="slider"></span>
            </label>

            {/* User Profile Button - Styled like the image */}
            <div className="user-dropdown-container" ref={userButtonRef}>
              <button
                className="user-profile-button"
                onClick={toggleUserDropdown}
                aria-expanded={showUserDropdown}
                aria-haspopup="true"
              >
                <div className="profile-info-text">
                  <span className="profile-name">{currentUser.full_name || `${currentUser.first_name} ${currentUser.last_name}`}</span>
                </div>
                <div className="profile-avatar">
                  <span className="avatar-initials">
                    {currentUser.first_name?.[0]}{currentUser.last_name?.[0]}
                  </span>
                  <div className="avatar-status-indicator"></div>
                </div>
              </button>

              {/* Dropdown Menu - Rendered via Portal */}
              {showUserDropdown && ReactDOM.createPortal(
                <div className="user-dropdown-menu" style={dropdownStyle}>
                  <div className="dropdown-header">
                    <div className="dropdown-user-info">
                      <div className="dropdown-avatar">
                        <span className="user-initials">
                          {currentUser.first_name?.[0]}{currentUser.last_name?.[0]}
                        </span>
                      </div>
                      <div className="dropdown-user-details">
                        <div className="dropdown-user-name">{currentUser.full_name}</div>
                        <div className="dropdown-user-email">{currentUser.email}</div>
                        <div className="dropdown-user-role">
                          {currentUser.role === 'admin' ? 'Office Administrator' : 'Field Technician'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="dropdown-divider"></div>

                  <div className="dropdown-section">
                    <button
                      className="dropdown-item"
                      onClick={handlePasswordChange}
                    >
                      <i className="fas fa-key item-icon"></i>
                      <span className="item-text">Change Password</span>
                      <span className="item-description">Update your account security</span>
                    </button>

                    <Link
                      to="/"
                      className="dropdown-item"
                      onClick={() => setShowUserDropdown(false)}
                    >
                      <i className="fas fa-tachometer-alt item-icon"></i>
                      <span className="item-text">Dashboard</span>
                      <span className="item-description">Go to main dashboard</span>
                    </Link>

                    {currentUser.role === 'admin' && (
                      <>
                        <Link
                          to="/users"
                          className="dropdown-item admin-item"
                          onClick={() => setShowUserDropdown(false)}
                        >
                          <i className="fas fa-users-cog item-icon"></i>
                          <span className="item-text">Manage Users</span>
                          <span className="item-description">User administration</span>
                          <i className="fas fa-crown admin-indicator"></i>
                        </Link>
                      </>
                    )}
                  </div>

                  <div className="dropdown-divider"></div>

                  <div className="dropdown-section">
                    <button
                      className="dropdown-item logout-item"
                      onClick={handleLogout}
                    >
                      <i className="fas fa-sign-out-alt item-icon"></i>
                      <span className="item-text">Sign Out</span>
                      <span className="item-description">Secure logout</span>
                    </button>
                  </div>
                </div>,
                document.body
              )}
            </div>
          </div>
        )}

        {/* Mobile Menu Toggle */}
        <button
          className="mobile-menu-toggle"
          onClick={toggleMobileMenu}
          aria-expanded={showMobileMenu}
          aria-label="Toggle mobile menu"
        >
          <div className={`hamburger ${showMobileMenu ? 'active' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>
      </div>

      {/* Mobile Navigation Menu */}
      {showMobileMenu && (
        <div className="mobile-navigation">
          <div className="mobile-nav-content">
            {/* Mobile User Info */}
            {isAuthenticated && currentUser && (
              <div className="mobile-user-section">
                <div className="mobile-user-info">
                  <div className="mobile-user-avatar">
                    <span className="user-initials">
                      {currentUser.first_name?.[0]}{currentUser.last_name?.[0]}
                    </span>
                  </div>
                  <div className="mobile-user-details">
                    <div className="mobile-user-name">{currentUser.full_name}</div>
                    <div className="mobile-user-role">
                      {currentUser.role === 'admin' ? 'Administrator' : 'Field Technician'}
                    </div>
                  </div>
                </div>
                <div className="mobile-user-actions">
                  <button
                    className="mobile-action-button"
                    onClick={handlePasswordChange}
                    title="Change Password"
                  >
                    <i className="fas fa-key"></i>
                  </button>
                  <button
                    className="mobile-action-button logout"
                    onClick={handleLogout}
                    title="Sign Out"
                  >
                    <i className="fas fa-sign-out-alt"></i>
                  </button>
                </div>
              </div>
            )}

            {/* Mobile Navigation Items */}
            <nav className="mobile-nav-list">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`mobile-nav-item ${isActiveRoute(item.path) ? 'active' : ''} ${
                    item.adminOnly ? 'admin-only' : ''
                  }`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  <div className="mobile-nav-icon">
                    <i className={item.icon}></i>
                    {item.adminOnly && userNotificationCount > 0 && (
                      <span className="mobile-admin-indicator notification-dot">
                        <i className="fas fa-crown"></i>
                      </span>
                    )}
                  </div>
                  <div className="mobile-nav-content"> {/* This was named .mobile-nav-content, ensuring correct nesting from original CSS */}
                    <span className="mobile-nav-label">{item.label}</span>
                    {item.adminOnly && (
                      <span className="mobile-nav-description">Admin Only</span>
                    )}
                  </div>
                  <div className="mobile-nav-arrow">
                    <i className="fas fa-chevron-right"></i>
                  </div>
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Mobile Menu Overlay */}
      {showMobileMenu && (
        <div 
          className="mobile-menu-overlay"
          onClick={() => setShowMobileMenu(false)}
        ></div>
      )}
    </header>
  );
};

export default Header;